/**
 * controls.tsx — reusable filter controls for the Talent Pool (ported from the Juicebox replica,
 * restyled in plain modern Tailwind). Three primitive field shapes drive the whole catalog:
 *   • value slices  → chips (AutocompleteInput / ResolveInput) + a Similarity dial (+ When / match-mode)
 *   • enums         → FilterSelect (single or multi)
 *   • numeric ranges→ RangeRow ({gte,lte})
 */

import { useEffect, useRef, useState } from 'react';
import { X, Check, ChevronDown, Info } from 'lucide-react';
import { resolveEntity } from '@/lib/talentPool.api';
import type { Opt } from './tpEnums';
import type { Range } from './queryTree';

const ACCENT = '#0D1830';

// ── chips ──
export function FilterChip({ label, onRemove }: { label: string; onRemove?: () => void }) {
    return (
        <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-[#0D1830]/10 px-2.5 py-0.5 text-[13px] font-medium text-[#1C2B4D]">
            <span className="truncate">{label}</span>
            {onRemove && (
                <button type="button" onClick={onRemove} className="shrink-0 text-[#1C2B4D]/70 hover:text-[#1C2B4D]" aria-label="Remove">
                    <X size={12} />
                </button>
            )}
        </span>
    );
}

export function Checkbox({ checked, onChange, size = 16 }: { checked: boolean; onChange: (v: boolean) => void; size?: number }) {
    return (
        <button
            type="button"
            role="checkbox"
            aria-checked={checked}
            onClick={() => onChange(!checked)}
            className={`inline-flex shrink-0 items-center justify-center rounded-[5px] border transition-colors ${
                checked ? 'border-[#0D1830] bg-[#0D1830] text-white' : 'border-zinc-300 bg-white text-transparent hover:border-zinc-400'
            }`}
            style={{ width: size, height: size }}
        >
            <Check size={size - 5} strokeWidth={3} />
        </button>
    );
}

// ── value chips input (type to add, Enter, click X to remove) ──
export function AutocompleteInput({
    placeholder,
    values,
    onAdd,
    onRemove,
}: {
    placeholder: string;
    values: { label: string }[];
    onAdd: (text: string) => void;
    onRemove: (i: number) => void;
}) {
    const [text, setText] = useState('');
    const add = () => {
        const t = text.trim();
        if (t) {
            onAdd(t);
            setText('');
        }
    };
    return (
        <div>
            <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        add();
                    }
                }}
                placeholder={placeholder}
                className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-[13px] text-zinc-900 outline-none placeholder:text-zinc-400 focus:border-[#0D1830]"
            />
            {values.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {values.map((v, i) => (
                        <FilterChip key={`${v.label}-${i}`} label={v.label} onRemove={() => onRemove(i)} />
                    ))}
                </div>
            )}
        </div>
    );
}

// ── custom dropdown (single / multi) ──
export function FilterSelect({
    value,
    options,
    onChange,
    placeholder = 'Any',
    multiple,
    minWidth,
}: {
    value: string | string[];
    options: Opt[];
    onChange: (v: string | string[]) => void;
    placeholder?: string;
    multiple?: boolean;
    minWidth?: number;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    const selectedArr = Array.isArray(value) ? value : value ? [value] : [];
    const display = multiple
        ? selectedArr.map((v) => options.find((o) => o.value === v)?.label || v).join(', ')
        : options.find((o) => o.value === value)?.label;

    const choose = (v: string) => {
        if (multiple) {
            const arr = selectedArr.includes(v) ? selectedArr.filter((x) => x !== v) : [...selectedArr, v];
            onChange(arr);
        } else {
            onChange(v);
            setOpen(false);
        }
    };

    return (
        <div className="relative" ref={ref} style={minWidth ? { minWidth } : undefined}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="flex w-full items-center justify-between gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-left text-[13px] outline-none hover:border-zinc-300 focus:border-[#0D1830]"
            >
                <span className={`truncate ${display ? 'text-zinc-900' : 'text-zinc-400'}`}>{display || placeholder}</span>
                <ChevronDown size={15} className="shrink-0 text-zinc-400" />
            </button>
            {open && (
                <div className="absolute left-0 right-0 z-30 mt-1 max-h-[260px] overflow-y-auto rounded-lg border border-zinc-200 bg-white py-1 shadow-lg">
                    {options.map((o) => {
                        const sel = selectedArr.includes(o.value);
                        return (
                            <button
                                key={o.value}
                                type="button"
                                onClick={() => choose(o.value)}
                                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] hover:bg-zinc-50 ${sel ? 'text-[#1C2B4D]' : 'text-zinc-700'}`}
                            >
                                {multiple && (
                                    <span
                                        className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[4px] border ${
                                            sel ? 'border-[#0D1830] bg-[#0D1830] text-white' : 'border-zinc-300'
                                        }`}
                                    >
                                        {sel && <Check size={10} strokeWidth={3} />}
                                    </span>
                                )}
                                <span className="min-w-0 flex-1 truncate">{o.label}</span>
                                {!multiple && sel && <Check size={14} className="shrink-0 text-[#0D1830]" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ── similarity dial (exact → broad) ──
export function Similarity({ value, onChange }: { value: number; onChange: (v: number) => void }) {
    const label = value < 0.25 ? 'Exact' : value < 0.5 ? 'Close' : value < 0.75 ? 'Semantic' : 'Broad';
    return (
        <div className="mt-2 flex items-center gap-2">
            <span className="w-[88px] shrink-0 text-[11px] text-zinc-500">Match: {label}</span>
            <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="h-1 flex-1 cursor-pointer accent-[#0D1830]"
            />
            <span className="w-9 text-right text-[11px] tabular-nums text-zinc-400">{value.toFixed(2)}</span>
        </div>
    );
}

// ── segmented pill toggle ──
export function Segmented({ value, options, onChange }: { value: string; options: Opt[]; onChange: (v: string) => void }) {
    return (
        <div className="inline-flex overflow-hidden rounded-lg border border-zinc-200 text-[12px]">
            {options.map((o) => (
                <button
                    key={o.value}
                    type="button"
                    onClick={() => onChange(o.value)}
                    className={`px-2.5 py-1.5 font-medium transition-colors ${
                        value === o.value ? 'bg-[#0D1830] text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'
                    }`}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

export function WhenToggle({ value, onChange }: { value?: string; onChange: (v: 'current' | 'past' | 'any') => void }) {
    return (
        <Segmented
            value={value || 'any'}
            onChange={(v) => onChange(v as 'current' | 'past' | 'any')}
            options={[
                { value: 'any', label: 'Ever' },
                { value: 'current', label: 'Currently' },
                { value: 'past', label: 'Previously' },
            ]}
        />
    );
}

// ── numeric range ──
export function RangeRow({ value, onChange, unit = 'yrs' }: { value: Range; onChange: (v: Range) => void; unit?: string }) {
    const num = (s: string) => (s === '' ? undefined : Number(s));
    return (
        <div className="flex items-center gap-2 text-[13px]">
            <input
                type="number"
                value={value.gte ?? ''}
                onChange={(e) => onChange({ ...value, gte: num(e.target.value) })}
                placeholder="min"
                className="w-20 rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-zinc-900 outline-none focus:border-[#0D1830]"
            />
            <span className="text-zinc-400">to</span>
            <input
                type="number"
                value={value.lte ?? ''}
                onChange={(e) => onChange({ ...value, lte: num(e.target.value) })}
                placeholder="max"
                className="w-20 rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-zinc-900 outline-none focus:border-[#0D1830]"
            />
            {unit && <span className="text-[12px] text-zinc-400">{unit}</span>}
        </div>
    );
}

// ── entity-resolve typeahead → canonical chips (cities / universities) ──
export function ResolveInput({
    field,
    placeholder,
    values,
    onChange,
}: {
    field: string;
    placeholder: string;
    values: string[];
    onChange: (vals: string[]) => void;
}) {
    const [text, setText] = useState('');
    const [cands, setCands] = useState<{ canonical: string; hint?: string }[]>([]);
    const [open, setOpen] = useState(false);
    const t = useRef<number | undefined>(undefined);

    useEffect(() => {
        if (text.trim().length < 2) {
            setCands([]);
            return;
        }
        window.clearTimeout(t.current);
        t.current = window.setTimeout(async () => {
            try {
                const r = await resolveEntity(field, text.trim());
                setCands(
                    r.map((c) => ({
                        canonical: c.canonical || c.label || '',
                        hint: c.via || (typeof c.confidence === 'number' ? `${Math.round(c.confidence * 100)}%` : undefined),
                    })).filter((c) => c.canonical),
                );
                setOpen(true);
            } catch {
                /* ignore */
            }
        }, 200);
        return () => window.clearTimeout(t.current);
    }, [text, field]);

    const add = (canonical: string) => {
        const v = canonical.trim();
        if (v && !values.includes(v)) onChange([v, ...values]);
        setText('');
        setCands([]);
        setOpen(false);
    };

    return (
        <div className="relative">
            <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        add(cands[0]?.canonical || text.trim());
                    }
                }}
                onFocus={() => cands.length && setOpen(true)}
                placeholder={placeholder}
                className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-[13px] text-zinc-900 outline-none placeholder:text-zinc-400 focus:border-[#0D1830]"
            />
            {open && cands.length > 0 && (
                <div className="absolute left-0 right-0 z-30 mt-1 max-h-[220px] overflow-y-auto rounded-lg border border-zinc-200 bg-white py-1 shadow-lg">
                    {cands.map((c, i) => (
                        <button
                            key={`${c.canonical}-${i}`}
                            type="button"
                            onClick={() => add(c.canonical)}
                            className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[13px] text-zinc-700 hover:bg-zinc-50"
                        >
                            <span className="min-w-0 flex-1 truncate">{c.canonical}</span>
                            {c.hint && <span className="shrink-0 text-[11px] text-zinc-400">{c.hint}</span>}
                        </button>
                    ))}
                </div>
            )}
            {values.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {values.map((v, i) => (
                        <FilterChip key={`${v}-${i}`} label={v} onRemove={() => onChange(values.filter((_, x) => x !== i))} />
                    ))}
                </div>
            )}
        </div>
    );
}

// ── small shared bits used by the modal ──
export function FilterHeading({ heading, active, tooltip }: { heading: string; active?: boolean; tooltip?: string }) {
    return (
        <div className="mb-1.5 flex items-center gap-1.5">
            <span className={`text-[13px] font-medium ${active ? 'text-[#1C2B4D]' : 'text-zinc-800'}`}>{heading}</span>
            {tooltip && (
                <span title={tooltip} className="cursor-help text-zinc-400">
                    <Info size={13} />
                </span>
            )}
        </div>
    );
}

export function ClearAll({ onClick }: { onClick: () => void }) {
    return (
        <button type="button" onClick={onClick} className="text-[12px] font-medium text-[#0D1830] hover:underline">
            Clear
        </button>
    );
}

/** Exclude (NOT) toggle — turns a field/condition into an exclusion. Red is semantic, not the theme accent. */
export function ExcludeToggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
    return (
        <button
            type="button"
            onClick={() => onChange(!on)}
            title="Exclude candidates matching this"
            className={`rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                on ? 'border border-red-200 bg-red-50 text-red-600' : 'border border-zinc-200 text-zinc-500 hover:bg-zinc-50'
            }`}
        >
            {on ? 'Excluding' : 'Exclude'}
        </button>
    );
}

export { ACCENT };
